import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useAuth } from "@/lib/auth";

// Marketing nav for the logged-out site. Surfaces the content pages (esp. the
// Blog) that were previously only reachable from the footer.
// `labelKey` is a translation key (resolved at render via t()), not user-facing text.
const NAV_LINKS = [
  { labelKey: "nav.links.findTutor", to: "/find-a-tutor/english" },
  { labelKey: "nav.links.becomeTutor", to: "/become-a-tutor/english" },
  { labelKey: "nav.links.blog", to: "/blog" },
  { labelKey: "nav.links.faq", to: "/faq" },
];

const Header = () => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const { user } = useAuth();
  // Same role → landing mapping as ProtectedRoute's dashboardPath / the SignIn redirect.
  // `user` is null during SSR and while the me-query is pending, so guest CTAs are the
  // server-rendered and first-hydration state — no hydration mismatch.
  const dashboardPath = user?.role === "tutor" ? "/tutor/classroom" : "/student/classroom";

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group min-h-11">
          <img src="/images/logo/navbar-logo.svg" alt={t("nav.logoAlt")} className="w-10 h-10 rounded-xl" />
          {/* The brand, never machine-translated. */}
          <span translate="no" className="text-xl font-display font-bold text-foreground">Jynio</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {t(link.labelKey)}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LanguageSwitcher variant="compact" />
          <div className="hidden sm:flex items-center gap-3">
            {user ? (
              <Button variant="default" size="sm" asChild>
                <Link to={dashboardPath}>{t("nav.dashboard")}</Link>
              </Button>
            ) : (
              <>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/signin">{t("nav.signIn")}</Link>
                </Button>
                <Button variant="default" size="sm" asChild>
                  <Link to="/signup">{t("nav.getStarted")}</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile menu toggle */}
          <button
            type="button"
            className="md:hidden p-3 -mr-3 text-foreground rounded-lg focus-ring"
            aria-label={menuOpen ? t("nav.closeMenu") : t("nav.openMenu")}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <nav className="md:hidden border-t border-border bg-background px-4 py-4 space-y-1">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="block rounded-md px-3 py-3 text-sm font-medium text-foreground hover:bg-muted"
              onClick={() => setMenuOpen(false)}
            >
              {t(link.labelKey)}
            </Link>
          ))}
          <div className="flex gap-3 pt-3">
            {user ? (
              <Button variant="default" size="sm" className="flex-1" asChild>
                <Link to={dashboardPath} onClick={() => setMenuOpen(false)}>{t("nav.dashboard")}</Link>
              </Button>
            ) : (
              <>
                <Button variant="ghost" size="sm" className="flex-1" asChild>
                  <Link to="/signin" onClick={() => setMenuOpen(false)}>{t("nav.signIn")}</Link>
                </Button>
                <Button variant="default" size="sm" className="flex-1" asChild>
                  <Link to="/signup" onClick={() => setMenuOpen(false)}>{t("nav.getStarted")}</Link>
                </Button>
              </>
            )}
          </div>
          <div className="pt-2">
            <LanguageSwitcher className="w-full" />
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;
